import {
  listenToMcpAppOpenLinkHandlerUpdates,
  requestMcpAppOpenLink,
  requestMcpAppOpenLinkHandlerRegistration,
  type McpAppOpenLinkRequest,
} from '@trimble-agentic-external-npm-local/agentic-platform-sdk-iframe-typescript';
import type { McpAppOpenLinkCallback } from '@trimble-agentic-internal-npm-local/agentic-chat-ui';
import { useEffect, useMemo, useState } from 'react';

/**
 * Bridges parent MCP app open-link handler registration into chat-ui's
 * `onMcpAppOpenLink` prop. When the host registers a handler via
 * `listenToChatUi` / `updateMcpAppOpenLinkHandler`, open-link requests
 * are forwarded to the parent; otherwise chat-ui uses its default https policy.
 */
export const useMcpAppOpenLink = (): McpAppOpenLinkCallback | undefined => {
  const [registered, setRegistered] = useState(false);

  useEffect(() => {
    let active = true;
    let receivedUpdate = false;

    void requestMcpAppOpenLinkHandlerRegistration()
      .then(isRegistered => {
        // Ignore the initial snapshot if a newer push already arrived, so a
        // late discovery resolution can't roll back registration state.
        if (active && !receivedUpdate) {
          setRegistered(isRegistered);
        }
      })
      .catch((error: unknown) => {
        console.error(
          'Failed to request MCP app open-link handler registration',
          error
        );
      });

    const unsubscribe = listenToMcpAppOpenLinkHandlerUpdates(isRegistered => {
      receivedUpdate = true;
      if (active) {
        setRegistered(isRegistered);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, []);

  return useMemo(
    () =>
      registered
        ? (request: McpAppOpenLinkRequest) => requestMcpAppOpenLink(request)
        : undefined,
    [registered]
  );
};
